import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  actOnTicket,
  assignTicket,
  changeTicketStatus,
  countTickets,
  createTicketComment,
  createTickets,
  dismissTicketNotifications,
  getTicket,
  getTicketAccess,
  listTicketComments,
  listTicketReports,
  listTickets,
  listTicketTypes,
  raiseDetectedTickets,
  readTicketNotifications,
  reportTicket,
  ticketsByIds,
  unassignTicket,
  updateTicket,
  updateTicketType,
} from '@/client'
import type {
  ListTicketsData,
  TicketActionRequest,
  TicketAssign,
  TicketCommentCreate,
  TicketCreate,
  TicketRead,
  TicketReportCreate,
  TicketStatusUpdate,
  TicketUpdate,
} from '@/client'

/**
 * Server state for tickets: the list and its counts, one ticket, its
 * comments, the type catalogue and what the caller may do.
 *
 * One module because it is one backend aggregate. Every mutation that returns
 * the ticket writes it into the detail cache — the response *is* the new
 * state — and invalidates the list and count prefixes, because a moved ticket
 * leaves one tab and lands in another, and guessing which is how a stale tab
 * survives.
 */

/** The list's query, exactly as the generated client takes it. */
export type TicketListParams = NonNullable<ListTicketsData['query']>

/** The counts take the list's filters minus the view, the page and the order. */
export type TicketCountParams = Omit<TicketListParams, 'view' | 'page' | 'limit' | 'sort' | 'order'>

/** Thirty-one types; one page of the API's maximum holds all of them. */
const TYPES_LIMIT = 100

/** Query keys, defined once. See CLAUDE.md §4.1 for why. */
export const ticketKeys = {
  all: ['tickets'] as const,
  lists: () => [...ticketKeys.all, 'list'] as const,
  list: (params: TicketListParams) => [...ticketKeys.lists(), params] as const,
  countsAll: () => [...ticketKeys.all, 'counts'] as const,
  counts: (params: TicketCountParams) => [...ticketKeys.countsAll(), params] as const,
  details: () => [...ticketKeys.all, 'detail'] as const,
  detail: (id: string) => [...ticketKeys.details(), id] as const,
  comments: (id: string, page: number) => [...ticketKeys.detail(id), 'comments', page] as const,
  reports: (id: string) => [...ticketKeys.detail(id), 'reports'] as const,
  // Under the list prefix: whatever invalidates the lists refreshes these too.
  pinned: (ids: readonly string[]) => [...ticketKeys.lists(), 'pinned', ids] as const,
  reportedSources: () => [...ticketKeys.lists(), 'reported-sources'] as const,
  types: () => [...ticketKeys.all, 'types'] as const,
  access: () => [...ticketKeys.all, 'access'] as const,
}

export function useTicketAccess() {
  return useQuery({
    queryKey: ticketKeys.access(),
    queryFn: async () => (await getTicketAccess({ throwOnError: true })).data,
    // Grants change with a new token, not while a page is open.
    staleTime: 5 * 60_000,
  })
}

export function useTicketTypes() {
  return useQuery({
    queryKey: ticketKeys.types(),
    queryFn: async () =>
      (await listTicketTypes({ query: { limit: TYPES_LIMIT }, throwOnError: true })).data,
    staleTime: 5 * 60_000,
  })
}

export function useTickets(params: TicketListParams, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.list(params),
    enabled,
    queryFn: async () => (await listTickets({ query: params, throwOnError: true })).data,
    // Paging keeps the previous page on screen until the next arrives, so the
    // pager does not collapse to a skeleton and jump on every click.
    placeholderData: keepPreviousData,
  })
}

/** `enabled` off for counts a page shows only to some readers. */
export function useTicketCounts(params: TicketCountParams, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.counts(params),
    queryFn: async () => (await countTickets({ query: params, throwOnError: true })).data,
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: async () => (await getTicket({ path: { ticket_id: id }, throwOnError: true })).data,
  })
}

/**
 * The tickets someone pinned, as list rows, in one request — VentaHub's
 * ``/by-ids``. The server applies the list's own visibility, so a pin shows
 * exactly what the list would and a pin whose ticket is gone, hidden or out
 * of reach simply does not come back. The list itself is asked to leave the
 * same ids out (``pinned_ids``), so page 1 is still a full page.
 */
export function usePinnedTickets(ids: readonly string[]) {
  const query = useQuery({
    queryKey: ticketKeys.pinned(ids),
    queryFn: async () =>
      (await ticketsByIds({ query: { ids: [...ids] }, throwOnError: true })).data,
    enabled: ids.length > 0,
    placeholderData: keepPreviousData,
  })
  return {
    tickets: ids.length > 0 ? (query.data ?? []) : [],
    isPending: ids.length > 0 && query.isPending,
  }
}

/** The reports (T8) filed on a ticket, newest first. */
export function useTicketReports(id: string) {
  return useQuery({
    queryKey: ticketKeys.reports(id),
    queryFn: async () =>
      (await listTicketReports({ path: { ticket_id: id }, throwOnError: true })).data,
  })
}

/** Raise a report on a ticket: a T8 for the technical team. */
export function useReportTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: TicketReportCreate) =>
      (await reportTicket({ body, throwOnError: true })).data,
    onSuccess: (_result, body) => {
      // A new T8 belongs in some list, and its source now has a report.
      void queryClient.invalidateQueries({ queryKey: ticketKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ticketKeys.countsAll() })
      void queryClient.invalidateQueries({ queryKey: ticketKeys.reports(body.source_ticket_id) })
    },
  })
}

export function useTicketComments(id: string, page: number) {
  return useQuery({
    queryKey: ticketKeys.comments(id, page),
    queryFn: async () =>
      (
        await listTicketComments({
          path: { ticket_id: id },
          query: { page, limit: 50 },
          throwOnError: true,
        })
      ).data,
    placeholderData: keepPreviousData,
  })
}

/**
 * What every ticket mutation does with its answer: the detail cache gets the
 * ticket as returned, and every list and count is refetched.
 */
function useSettle() {
  const queryClient = useQueryClient()
  return async (ticket?: TicketRead) => {
    if (ticket) queryClient.setQueryData(ticketKeys.detail(ticket.id), ticket)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ticketKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: ticketKeys.countsAll() }),
    ])
  }
}

export function useCreateTickets() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async (body: TicketCreate) =>
      (await createTickets({ body, throwOnError: true })).data,
    onSuccess: () => settle(),
  })
}

export function useUpdateTicket(id: string) {
  const settle = useSettle()
  return useMutation({
    mutationFn: async (body: TicketUpdate) =>
      (await updateTicket({ path: { ticket_id: id }, body, throwOnError: true })).data,
    onSuccess: (ticket) => settle(ticket),
  })
}

export function useAssignTicket() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: TicketAssign }) =>
      (await assignTicket({ path: { ticket_id: id }, body, throwOnError: true })).data,
    onSuccess: (ticket) => settle(ticket),
  })
}

export function useUnassignTicket() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async (id: string) =>
      (await unassignTicket({ path: { ticket_id: id }, throwOnError: true })).data,
    onSuccess: (ticket) => settle(ticket),
  })
}

export function useChangeTicketStatus() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: TicketStatusUpdate }) =>
      (await changeTicketStatus({ path: { ticket_id: id }, body, throwOnError: true })).data,
    onSuccess: (ticket) => settle(ticket),
  })
}

/** For type layouts: hand item decisions to the ticket's type. */
export function useActOnTicket(id: string) {
  const settle = useSettle()
  return useMutation({
    mutationFn: async (body: TicketActionRequest) =>
      (await actOnTicket({ path: { ticket_id: id }, body, throwOnError: true })).data,
    onSuccess: (result) => settle(result.ticket),
  })
}

export function useAddTicketComment(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: TicketCommentCreate) =>
      (await createTicketComment({ path: { ticket_id: id }, body, throwOnError: true })).data,
    // Every page of the thread: a new comment lands on the last one, and
    // which page that is depends on how many there were.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...ticketKeys.detail(id), 'comments'] }),
  })
}

export function useMarkTicketNotificationsRead() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async ({ ids, isRead }: { ids: string[]; isRead: boolean }) =>
      (
        await readTicketNotifications({
          body: { ticket_ids: ids, is_read: isRead },
          throwOnError: true,
        })
      ).data,
    onSuccess: () => settle(),
  })
}

export function useDismissTicketNotifications() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async (ids: string[]) =>
      (await dismissTicketNotifications({ body: { ticket_ids: ids }, throwOnError: true })).data,
    onSuccess: () => settle(),
  })
}

/**
 * Run a type's detector now, as its scheduled job would — `tickets:edit`.
 * Whatever it raises lands in the lists, so they refetch.
 */
export function useRaiseDetectedTickets() {
  const settle = useSettle()
  return useMutation({
    mutationFn: async (code: string) =>
      (await raiseDetectedTickets({ path: { ticket_type_id: code }, throwOnError: true })).data,
    onSuccess: () => settle(),
  })
}

export function useUpdateTicketTypeEta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ code, eta }: { code: string; eta: number }) =>
      (
        await updateTicketType({
          path: { ticket_type_id: code },
          body: { eta },
          throwOnError: true,
        })
      ).data,
    // The ETA is on every list row and every detail, so everything tickets.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
  })
}
